import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 🎨 CATIA-Style Weld Symbol (Kaynak Sembolü)
 * Welding annotation with weld type and size
 */

export class FikoKaynakSembol extends THREE.Group {
    override type = 'FikoKaynakSembol';

    // 🔴 SABİT BOYUT - Kamera zoom'una göre scale ayarlanır
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private pozisyon: THREE.Vector3;
    private kaynakTipi: string;
    private boyut: number;
    private renk: string = '#795548';

    private sprite: THREE.Sprite | null = null;
    private leaderLine: THREE.Line | null = null;
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        pozisyon: THREE.Vector3,
        kaynakTipi: string = 'fillet',
        boyut: number = 5
    ) {
        super();

        this.pozisyon = pozisyon.clone();
        this.kaynakTipi = kaynakTipi;
        this.boyut = boyut;

        this.canvas = document.createElement('canvas');
        this.canvas.width = 128;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = 'Kaynak Sembolü';
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;
        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.sprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.sprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createLeaderLine();
        this.createSprite();
    }

    private createLeaderLine(): void {
        const endPoint = this.pozisyon.clone().add(new THREE.Vector3(0, -0.5, 0));

        const geometry = new THREE.BufferGeometry().setFromPoints([
            this.pozisyon,
            endPoint
        ]);

        const material = new THREE.LineBasicMaterial({
            color: new THREE.Color(this.renk),
            linewidth: 2,
            depthTest: false,
            depthWrite: false
        });

        this.leaderLine = new THREE.Line(geometry, material);
        this.add(this.leaderLine);
    }

    private createSprite(): void {
        const ctx = this.context;
        const width = 120;
        const height = 60;

        this.canvas.width = width;
        this.canvas.height = height;
        ctx.clearRect(0, 0, width, height);

        // Background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.fillRect(5, 5, width - 10, height - 10);

        ctx.strokeStyle = this.renk;
        ctx.lineWidth = 2;
        ctx.strokeRect(5, 5, width - 10, height - 10);

        // Reference line (horizontal)
        ctx.beginPath();
        ctx.moveTo(15, height / 2);
        ctx.lineTo(width - 15, height / 2);
        ctx.stroke();

        // Weld symbol based on type
        ctx.lineWidth = 3;
        if (this.kaynakTipi === 'fillet') {
            // Right triangle
            ctx.beginPath();
            ctx.moveTo(width / 2 - 15, height / 2);
            ctx.lineTo(width / 2, height / 2);
            ctx.lineTo(width / 2, height / 2 - 15);
            ctx.stroke();
        } else if (this.kaynakTipi === 'butt') {
            // V shape
            ctx.beginPath();
            ctx.moveTo(width / 2 - 10, height / 2 - 10);
            ctx.lineTo(width / 2, height / 2);
            ctx.lineTo(width / 2 + 10, height / 2 - 10);
            ctx.stroke();
        } else {
            // Generic square
            ctx.strokeRect(width / 2 - 10, height / 2 - 10, 20, 20);
        }

        // Size text
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${this.boyut}mm`, width - 20, height / 2 + 15);

        this.texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.sprite = new THREE.Sprite(spriteMaterial);
        this.sprite.position.copy(this.pozisyon.clone().add(new THREE.Vector3(0, -0.8, 0)));
        this.sprite.scale.set(2, 1, 1);
        this.add(this.sprite);
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
        }
        if (this.sprite) {
            this.sprite.material.map?.dispose();
            this.sprite.material.dispose();
        }
        this.texture.dispose();
    }
}
